<template>
  <div class="style-popup">
    <div v-if="layerType">
  
      <h3>{{ layerTypeDisplayName }} 样式设置</h3>
            

      <el-form label-width="6.25rem">
        <!-- 所有图层类型共有的样式 -->
       <!-- 基本信息 -->
      <el-form label-width="6.25rem">
        <el-form-item label="名称">
          <el-input v-model="style.name" placeholder="输入图层名称" />
        </el-form-item>
        
        <el-form-item label="目的">
          <el-input v-model="style.purpose" placeholder="输入图层目的" />
        </el-form-item>

        <el-form-item label="国家">
          <el-input v-model="style.country" placeholder="输入所属国家" />
        </el-form-item>
        
        <el-form-item label="关联信息">
          <el-input 
            v-model="style.relatedInformation" 
            placeholder="输入关联信息" 
          />
        </el-form-item>
         <el-form-item label="备注">
          <el-input 
            v-model="style.remark" 
            placeholder="输入备注信息" 
          />
        </el-form-item>
        
        <!-- 几何信息 -->
        <template v-if="hasGeometryInfo">
          <el-form-item label="长度" v-if="hasLength">
            <el-input :value="geometryInfo.length" disabled>
              <template #append>km</template>
            </el-input>
          </el-form-item>
          
          <el-form-item label="面积" v-if="hasArea">
            <el-input :value="geometryInfo.area" disabled>
              <template #append>km²</template>
            </el-input>
          </el-form-item>
        </template>
      </el-form>
      <template v-if="layerType !== 'Marker'">
        <el-form-item label="边框颜色">
          <el-color-picker v-model="style.stroke.color" show-alpha />
        </el-form-item>
        
        <el-form-item label="边框宽度" v-if="hasStroke">
          <el-input-number v-model="style.stroke.width" :min="1" :max="10" />
        </el-form-item>
        
        <el-form-item label="边框样式" v-if="hasStroke">
          <el-select v-model="style.stroke.dashArray">
            <el-option label="实线" value="0"></el-option>
            <el-option label="虚线" value="5,5"></el-option>
            <el-option label="点线" value="1,5"></el-option>
          </el-select>
        </el-form-item>
      </template>
        <!-- 多边形/圆形特有的填充样式 -->
        <template v-if="hasFill">
          <el-form-item label="填充颜色">
            <el-color-picker v-model="style.fill.color" show-alpha />
          </el-form-item>
          
          <el-form-item label="填充透明度">
            <el-slider v-model="style.fill.opacity" :min="0" :max="1" :step="0.1" />
          </el-form-item>
        </template>
        
        <!-- 标记点特有的大小设置 -->
        <el-form-item label="标记颜色" v-if="layerType === 'Marker'">
          <el-color-picker v-model="style.marker.color" show-alpha />
        </el-form-item>
        <el-form-item label="标记大小" v-if="layerType === 'Marker'">
          <el-input-number v-model="style.marker.size" :min="8" :max="32" />
        </el-form-item>
      </el-form>
      
      <div class="popup-actions">
        <el-button type="danger" @click="onDelete">删除图层</el-button>
        <el-button @click="onClose">关闭</el-button>
      </div>
    </div>
    
    <div v-else>
      <p>无法识别的图层类型</p>
    </div>
  </div>
</template>

<script setup>
import { defineProps, ref, computed, watch, onMounted } from 'vue';

const props = defineProps({
  layer: Object,
  layerType: String,
  style: Object,
  onStyleChange: Function,
  onDelete: Function,
  onClose: Function
});

// 计算属性
const layerTypeDisplayName = computed(() => {
  const names = {
    'Polygon': '多边形',
    'Polyline': '折线',
    'Circle': '圆形',
    'Marker': '标记点',
    'Rectangle': '矩形'
  };
  return names[props.layerType] || props.layerType;
});

const hasStroke = computed(() => 
  ['Polygon', 'Polyline', , 'Rectangle'].includes(props.layerType)
);

const hasFill = computed(() => 
  ['Polygon', 'Circle', 'Rectangle'].includes(props.layerType)
);

const hasLength = computed(() =>
  ['Polyline'].includes(props.layerType)
);

const hasArea = computed(() =>
  ['Polygon', 'Circle', 'Rectangle'].includes(props.layerType)
);

const hasGeometryInfo = computed(() => hasLength.value || hasArea.value);

// 几何信息计算
const geometryInfo = computed(() => {
  const info = { length: 0, area: 0 };
  if (!props.layer) return info;
  try {
    if (props.layerType === 'Polyline') {
      const latLngs = props.layer.getLatLngs();
      if (latLngs.length > 0 && Array.isArray(latLngs[0])) {
        info.length = latLngs.reduce((total, segment) => {
          return total + segment.reduce((sum, point, i, arr) => 
            i > 0 ? sum + point.distanceTo(arr[i-1]) : 0, 0);
        }, 0);
      } else {
        info.length = latLngs.reduce((sum, point, i, arr) => 
          i > 0 ? sum + point.distanceTo(arr[i-1]) : 0, 0);
      }
      info.length = (info.length / 1000).toFixed(2);
      props.layer.options.length = info.length;
    }
    else if (props.layerType === 'Polygon' || props.layerType === 'Rectangle') {
      const latLngs = props.layer.getLatLngs()[0];
      info.length = (latLngs.reduce((sum, point, i, arr) => 
        i > 0 ? sum + point.distanceTo(arr[i-1]) : 0, 0) / 1000).toFixed(2);
      info.area = (L.GeometryUtil.geodesicArea(latLngs) / 1000000).toFixed(4);
      props.layer.options.area = info.area;
    }
    else if (props.layerType === 'Circle') {
      info.length = (2 * Math.PI * props.layer.getRadius() / 1000).toFixed(2);
      info.area = (Math.PI * Math.pow(props.layer.getRadius(), 2) / 1000000).toFixed(4);
      props.layer.options.area = info.area;
    }
  } catch (e) {
    console.error('计算几何信息出错:', e);
  }
  
  return info;
});

// 默认样式
const style = ref({
  name: props.style?.name || '',
  purpose: props.style?.purpose || '',
  relatedInformation: props.style?.relatedInformation || '',
  country: props.style?.country || '',
  remark: props.style?.remark || '',
  stroke: {
    color: props.style?.stroke?.color || '#3388ff',
    width: props.style?.stroke?.width || 3,
    dashArray: props.style?.stroke?.dashArray || '0'
  },
  fill: {
    color: props.style?.fill?.color || '#3388ff',
    opacity: props.style?.fill?.opacity || 0.2
  },
  marker: {
    size: props.style?.marker?.size || 16,
    color: props.style?.marker?.color || '#3388ff'
  }
});

// 从图层获取当前样式
onMounted(() => {
  if (props.layer && props.layer.properties) {
    const layerProps = props.layer.properties;
    const layerStyle = layerProps.style || {};
    
    // 更新样式引用
    style.value = {
      name: layerProps.name || '',
      purpose: layerProps.purpose || '',
      relatedInformation: layerProps.relatedInformation || '',
      country: layerProps.country || '',
      remark: layerProps.remark || '',
      stroke: {
        ...style.value.stroke,
        color: layerStyle.stroke?.color || layerStyle.strokeColor || '#3388ff',
        width: layerStyle.stroke?.width || layerStyle.strokeWeight || 3,
        dashArray: layerStyle.stroke?.dashArray || layerStyle.dashArray || '0'
      },
      fill: {
        ...style.value.fill,
        color: layerStyle.fill?.color || layerStyle.fillColor || '#3388ff',
        opacity: layerStyle.fill?.opacity || layerStyle.fillOpacity || 0.2
      },
      marker: {
        size: layerStyle.marker?.size || 16,
        color: layerStyle.marker?.color || '#3388ff' 
      }
    };
  }
});

// 监听样式变化
watch(style, (newVal) => {
  if (props.layer) {
    // 更新图层属性
    props.layer.properties = {
      ...(props.layer.properties || {}),
      name: newVal.name,
      purpose: newVal.purpose,
      relatedInformation: newVal.relatedInformation,
      country: newVal.country,
      remark: newVal.remark
    };
  }
  if (props.onStyleChange) {
    props.onStyleChange(newVal);
  }
}, { deep: true });
</script>

<style scoped>
.style-popup {
  padding: 15px;
  min-width: 320px;
}

.popup-actions {
  margin-top: 20px;
  display: flex;
  justify-content: space-between;
}

h3 {
  margin-top: 0;
  margin-bottom: 15px;
  color: var(--el-color-primary);
}

.attribute-info {
  margin-left: 10px;
  font-size: 12px;
  color: #999;
}

.el-divider {
  margin: 15px 0;
}
</style>